import { useCallback, useRef, useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import { Icon, Label } from '@susu/ui/components'

import { useNbaCard } from '../hooks/useNbaCard'

import NbaFeedbackSheet from './NbaFeedbackSheet'

import type { NbaFeedbackScore } from '../api/nbaCardApi'
import type { EnvironmentWithChina } from '../App'

const MAX_BULLETS = 3

export interface NbaFeedback {
  cardId: string
  rating: NbaFeedbackScore
  notes: string
}

interface NbaCardProps {
  accountNumber: string
  authToken?: string
  env: EnvironmentWithChina
  /** `NBA_cards_final` GrowthBook flag — off means the card never renders. */
  isEnabled: boolean
  /**
   * The associate already rated this card in the current profile visit — the
   * footer shows the thank-you line instead of the thumbs. Owned by the
   * caller so it survives tab switches but resets when the profile reopens.
   */
  hasSentFeedback?: boolean
  onFeedback?: (feedback: NbaFeedback) => void
}

/**
 * Next Best Action card — in-store only, behind the `NBA_cards_final`
 * GrowthBook flag (RP-6063), with a Beta label and thumbs up/down feedback
 * in the footer (RP-6164).
 *
 * Design: locked handoff spec (design_handoff_next_best_action) — hairline
 * border, white card, no title, the `recommended` icon inline with the
 * headline. That icon renders in its own brand-gradient fill regardless of
 * the design system's `color` prop — the one intentional departure from the
 * card's otherwise monochrome styling, signalling "AI-generated".
 */
export default function NbaCard({
  accountNumber,
  authToken,
  env,
  isEnabled,
  hasSentFeedback = false,
  onFeedback,
}: Readonly<NbaCardProps>) {
  const { t } = useI18N()
  const { data: card, isError } = useNbaCard({
    accountNumber,
    env,
    authToken,
    enabled: isEnabled,
  })
  const [pendingRating, setPendingRating] = useState<NbaFeedbackScore | null>(
    null,
  )
  // Read synchronously so a double tap on Send reports once, even before the
  // re-render that unmounts the sheet.
  const pendingRatingRef = useRef<NbaFeedbackScore | null>(null)
  const cardId = card?.cardId ?? ''

  const openSheet = (rating: NbaFeedbackScore) => {
    pendingRatingRef.current = rating
    setPendingRating(rating)
  }

  const handleSheetFinish = useCallback(
    (notes: string) => {
      const rating = pendingRatingRef.current
      if (!rating) return
      pendingRatingRef.current = null
      setPendingRating(null)
      onFeedback?.({ cardId, rating, notes })
    },
    [cardId, onFeedback],
  )

  if (!isEnabled || isError || !card) {
    return null
  }

  const bullets = card.bullets.slice(0, MAX_BULLETS)

  return (
    <section
      className="flex flex-col gap-10 rounded-md border border-border bg-surface p-16 max-lg:mx-16"
      data-testid="nba-card"
    >
      <div className="flex items-start gap-10">
        <Icon name="recommended" size="large" className="shrink-0" />
        <h2
          className="text-base leading-base font-medium tracking-heading text-text [text-wrap:pretty]"
          data-testid="nba-card-headline"
        >
          {card.action}
        </h2>
      </div>
      <ul role="list" className="flex list-none flex-col gap-4 pl-[34px]">
        {bullets.map((bullet, index) => (
          <li key={index} className="flex gap-8" data-testid="nba-card-bullet">
            <span
              aria-hidden="true"
              className="mt-[8px] h-3 w-3 shrink-0 rounded-full bg-text"
            />
            <span className="text-sm leading-sm font-light text-text [text-wrap:pretty]">
              {bullet}
            </span>
          </li>
        ))}
      </ul>
      <div
        className="flex min-h-44 items-center justify-between gap-8 border-t border-border pt-12"
        data-testid="nba-card-footer"
      >
        <Label
          size="s"
          color="grey"
          className="!text-text-secondary"
          text={t('nbaCard.beta')}
        />
        {hasSentFeedback ? (
          <p
            className="text-sm leading-sm font-light text-text-secondary"
            data-testid="nba-card-feedback-thanks"
          >
            {t('nbaCard.thanks')}
          </p>
        ) : (
          <div className="-my-12 -mr-12 flex items-center">
            <span className="mr-4 text-sm leading-sm font-light text-text-secondary">
              {t('nbaCard.useful')}
            </span>
            <button
              type="button"
              aria-label={t('nbaCard.ratePositive')}
              className="flex h-44 w-44 items-center justify-center rounded-full hover:bg-surface-hover"
              data-testid="nba-card-feedback-up"
              onClick={() => openSheet('good')}
            >
              <Icon name="thumbs-up" size={20} color="text" />
            </button>
            <button
              type="button"
              aria-label={t('nbaCard.rateNegative')}
              className="flex h-44 w-44 items-center justify-center rounded-full hover:bg-surface-hover"
              data-testid="nba-card-feedback-down"
              onClick={() => openSheet('bad')}
            >
              <Icon name="thumbs-down" size={20} color="text" />
            </button>
          </div>
        )}
      </div>
      {pendingRating && (
        <NbaFeedbackSheet rating={pendingRating} onFinish={handleSheetFinish} />
      )}
    </section>
  )
}
